/*
 * FAR Sticker Studio — material-aware high-variant finish bridge v5
 *
 * Purpose:
 * - Use Shopify's complete option-value map instead of the truncated
 *   product.variants array for high-variant finish availability.
 * - Show only customer-selectable finish/texture choices for each sticker family.
 * - Stabilize the first render after Sticker Studio's native initializer runs,
 *   so a valid choice never appears disabled until the customer changes product type.
 * - Preserve Shopify as the price/variant authority.
 *
 * This file does NOT alter product prices, variant IDs, SKUs, quantity tiers,
 * FAR Proof, size logic, cart properties, or production properties.
 */
(function () {
  'use strict';

  var PRODUCT_POLICIES = {
    'custom-vinyl-stickers': {
      finishes: ['matte', 'gloss'],
      defaultFinish: 'matte',
      allowUv: true
    },
    'holographic-stickers': {
      finishes: ['gloss', 'matte'],
      defaultFinish: 'gloss',
      allowUv: false
    },
    'glitter-stickers': {
      finishes: ['gloss'],
      defaultFinish: 'gloss',
      allowUv: false
    },
    'chrome-stickers': {
      finishes: ['gloss', 'matte'],
      defaultFinish: 'gloss',
      allowUv: false
    },
    'clear-stickers': {
      finishes: ['gloss', 'matte'],
      defaultFinish: 'gloss',
      allowUv: false
    },
    'economy-stickers': {
      finishes: ['non laminated'],
      defaultFinish: 'non laminated',
      allowUv: false
    },
    'qr-code-stickers': {
      finishes: ['matte', 'gloss'],
      defaultFinish: 'matte',
      allowUv: false
    }
  };

  var SETTLE_DELAYS = [0, 60, 180, 500];

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9.]+/g, ' ')
      .trim();
  }

  function forceHidden(element, hidden) {
    if (!element) return;

    var shouldHide = Boolean(hidden);

    if (element.hidden !== shouldHide) {
      element.hidden = shouldHide;
    }

    if (shouldHide) {
      if (element.getAttribute('aria-hidden') !== 'true') {
        element.setAttribute('aria-hidden', 'true');
      }
      if (element.style.getPropertyValue('display') !== 'none' ||
          element.style.getPropertyPriority('display') !== 'important') {
        element.style.setProperty('display', 'none', 'important');
      }
    } else {
      if (element.getAttribute('aria-hidden') !== 'false') {
        element.setAttribute('aria-hidden', 'false');
      }
      if (element.style.getPropertyValue('display')) {
        element.style.removeProperty('display');
      }
    }
  }

  function productHandle(productUrl) {
    try {
      var url = new URL(productUrl || '', window.location.origin);
      var parts = url.pathname.split('/').filter(Boolean);
      var productIndex = parts.indexOf('products');
      return productIndex >= 0 && parts[productIndex + 1] ? parts[productIndex + 1] : '';
    } catch (error) {
      return '';
    }
  }

  function readVariantData(root) {
    var script = root.querySelector('script[data-far-sticker-variants]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function findFinishOptionName(data) {
    var optionNames = Array.isArray(data && data.options) ? data.options : [];

    return optionNames.find(function (name) {
      var normalized = normalize(name);
      return normalized === 'finish' ||
        normalized.indexOf('finish') >= 0 ||
        normalized.indexOf('material') >= 0;
    }) || '';
  }

  function configuredOptionValues(data, optionName) {
    var map = data && data.option_value_ids && typeof data.option_value_ids === 'object'
      ? data.option_value_ids
      : {};
    var values = optionName && map[optionName] && typeof map[optionName] === 'object'
      ? map[optionName]
      : {};

    return Object.keys(values).reduce(function (result, name) {
      result[normalize(name)] = true;
      return result;
    }, {});
  }

  function uvBaseFinish(optionValue) {
    var normalized = normalize(optionValue);
    if (normalized.indexOf('uv method') < 0) return '';
    return normalize(normalized.replace(/\buv method\b/g, ''));
  }

  function uvConfiguredForFinish(configuredValues, finishValue) {
    var normalizedFinish = normalize(finishValue);
    if (!normalizedFinish) return false;

    return Object.keys(configuredValues).some(function (optionValue) {
      return uvBaseFinish(optionValue) === normalizedFinish;
    });
  }

  function choiceInput(choice) {
    return choice ? choice.querySelector('input[type="radio"]') : null;
  }

  function visibleInputByValue(choices, normalizedValue) {
    var result = null;

    Array.prototype.some.call(choices, function (choice) {
      var input = choiceInput(choice);
      if (
        input &&
        !choice.hidden &&
        !input.disabled &&
        normalize(input.value) === normalizedValue
      ) {
        result = input;
        return true;
      }
      return false;
    });

    return result;
  }

  function firstVisibleInput(choices) {
    var result = null;

    Array.prototype.some.call(choices, function (choice) {
      var input = choiceInput(choice);
      if (input && !choice.hidden && !input.disabled) {
        result = input;
        return true;
      }
      return false;
    });

    return result;
  }

  function setReviewTextureVisibility(root, visible) {
    var reviewTexture = root.querySelector('[data-far-sticker-review-texture]');
    if (reviewTexture && reviewTexture.parentElement) {
      forceHidden(reviewTexture.parentElement, !visible);
    }
  }

  function syncTexturePresentation(root, policy, configuredValues) {
    var textureGroup = root.querySelector('[data-far-sticker-texture-group]');
    if (!textureGroup) return;

    var standardInput = textureGroup.querySelector(
      '[data-far-sticker-texture][value="Standard"]'
    );
    var uvChoice = textureGroup.querySelector('[data-far-sticker-uv-choice]');
    var uvInput = uvChoice
      ? uvChoice.querySelector('[data-far-sticker-texture][value="UV Method"]')
      : null;
    var uvStatus = root.querySelector('[data-far-sticker-uv-status]');
    var selectedFinish = root.querySelector(
      'input[name*="far-sticker-finish"]:checked:not(:disabled)'
    );

    var allowUv = Boolean(policy && policy.allowUv);
    var customerSelectable = Boolean(
      allowUv &&
      selectedFinish &&
      uvConfiguredForFinish(configuredValues, selectedFinish.value) &&
      uvInput
    );

    if (!customerSelectable) {
      if (uvInput) {
        uvInput.checked = false;
        uvInput.disabled = true;
      }
      if (standardInput) {
        standardInput.disabled = false;
        standardInput.checked = true;
      }

      forceHidden(uvChoice, true);
      forceHidden(uvStatus, true);
      forceHidden(textureGroup, true);
      setReviewTextureVisibility(root, false);
      return;
    }

    if (uvInput) {
      uvInput.disabled = false;
    }
    if (standardInput) {
      standardInput.disabled = false;
    }

    forceHidden(textureGroup, false);
    forceHidden(uvChoice, false);
    forceHidden(uvStatus, false);
    setReviewTextureVisibility(root, true);
  }

  function allowedFinishSet(policy, configuredValues) {
    if (!policy) return null;

    return policy.finishes.reduce(function (set, finish) {
      if (configuredValues[finish]) {
        set[finish] = true;
      }
      return set;
    }, {});
  }

  function applyMaterialPolicy(root, options) {
    if (!root || root.nodeType !== 1) return;

    var data = readVariantData(root);
    var finishGroup = root.querySelector('[data-far-sticker-finish-group]');
    var finishChoices = root.querySelectorAll('[data-far-sticker-finish-choice]');
    if (!data || !finishGroup || !finishChoices.length) return;

    var finishOptionName = findFinishOptionName(data);
    var configuredValues = configuredOptionValues(data, finishOptionName);
    if (!finishOptionName || !Object.keys(configuredValues).length) return;

    var handle = productHandle(data.product_url);
    var policy = PRODUCT_POLICIES[handle] || null;
    var allowed = allowedFinishSet(policy, configuredValues);
    var visibleCount = 0;

    Array.prototype.forEach.call(finishChoices, function (choice) {
      var input = choiceInput(choice);
      var value = input ? normalize(input.value) : '';
      var configured = Boolean(input && configuredValues[value]);
      var policyAllowed = !allowed || Boolean(allowed[value]);
      var visible = configured && policyAllowed;

      forceHidden(choice, !visible);

      if (input) {
        input.disabled = !visible;
      }

      if (visible) {
        visibleCount += 1;
      }
    });

    if (visibleCount === 0) {
      /* Safety fallback: expose configured base finish values rather than strand checkout. */
      Array.prototype.forEach.call(finishChoices, function (choice) {
        var input = choiceInput(choice);
        var value = input ? normalize(input.value) : '';
        var configured = Boolean(input && configuredValues[value]);

        forceHidden(choice, !configured);

        if (input) {
          input.disabled = !configured;
        }

        if (configured) {
          visibleCount += 1;
        }
      });

      root.dataset.farStickerMaterialPolicyFallback = 'true';
    } else {
      delete root.dataset.farStickerMaterialPolicyFallback;
    }

    finishGroup.classList.toggle('is-single', visibleCount === 1);

    if (policy && visibleCount > 0) {
      policy.finishes.forEach(function (finishName) {
        Array.prototype.forEach.call(finishChoices, function (choice) {
          var input = choiceInput(choice);
          if (
            input &&
            !choice.hidden &&
            normalize(input.value) === finishName
          ) {
            finishGroup.appendChild(choice);
          }
        });
      });
    }

    var currentChecked = root.querySelector(
      'input[name*="far-sticker-finish"]:checked'
    );
    var currentValue = currentChecked ? normalize(currentChecked.value) : '';

    var desiredValue = normalize(root.dataset.farStickerDesiredFinish);
    var desiredInput = desiredValue
      ? visibleInputByValue(finishChoices, desiredValue)
      : null;

    if (!desiredInput && options && options.preserveCurrent && currentValue) {
      desiredInput = visibleInputByValue(finishChoices, currentValue);
    }

    if (!desiredInput && policy && policy.defaultFinish) {
      desiredInput = visibleInputByValue(finishChoices, policy.defaultFinish);
    }

    if (!desiredInput && currentValue) {
      desiredInput = visibleInputByValue(finishChoices, currentValue);
    }

    if (!desiredInput) {
      desiredInput = firstVisibleInput(finishChoices);
    }

    var selectionChanged = Boolean(
      desiredInput &&
      (!currentChecked || currentChecked !== desiredInput)
    );

    if (desiredInput) {
      Array.prototype.forEach.call(finishChoices, function (choice) {
        var input = choiceInput(choice);
        if (input) {
          input.checked = input === desiredInput;
        }
      });

      root.dataset.farStickerDesiredFinish = normalize(desiredInput.value);
    }

    root.dataset.farStickerMaterialPolicyReady = 'true';
    root.dataset.farStickerHighVariantFinishReady = 'true';
    root.dataset.farStickerMaterialPolicy = handle || 'default';

    syncTexturePresentation(root, policy, configuredValues);

    if (
      selectionChanged &&
      (!options || options.dispatchIfChanged !== false) &&
      desiredInput &&
      root.dataset.farStickerPolicyDispatching !== 'true'
    ) {
      root.dataset.farStickerPolicyDispatching = 'true';

      desiredInput.dispatchEvent(new Event('change', { bubbles: true }));

      window.setTimeout(function () {
        delete root.dataset.farStickerPolicyDispatching;
      }, 0);
    }
  }

  function scheduleSettle(root, preserveCurrent) {
    if (!root || root.nodeType !== 1) return;

    SETTLE_DELAYS.forEach(function (delay) {
      window.setTimeout(function () {
        if (!document.documentElement.contains(root)) return;

        applyMaterialPolicy(root, {
          preserveCurrent: Boolean(preserveCurrent),
          dispatchIfChanged: true
        });
      }, delay);
    });
  }

  function bindRoot(root) {
    if (!root || root.nodeType !== 1) return;

    if (root.dataset.farStickerPolicyEventsBound !== 'true') {
      root.dataset.farStickerPolicyEventsBound = 'true';

      root.addEventListener('change', function (event) {
        var target = event.target;
        if (!target || !target.matches) return;

        if (target.matches('input[name*="far-sticker-finish"]')) {
          if (!target.disabled) {
            root.dataset.farStickerDesiredFinish = normalize(target.value);
          }

          scheduleSettle(root, true);
          return;
        }

        if (target.matches('[data-far-sticker-texture]')) {
          scheduleSettle(root, true);
        }
      });
    }

    /* Apply now, then again after native Sticker Studio initialization settles. */
    applyMaterialPolicy(root, {
      preserveCurrent: false,
      dispatchIfChanged: true
    });
    scheduleSettle(root, false);
  }

  function applyAll(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;

    if (container.matches && container.matches('[data-far-sticker-studio]')) {
      bindRoot(container);
    }

    container.querySelectorAll('[data-far-sticker-studio]').forEach(bindRoot);
  }

  function boot() {
    applyAll(document);

    if (document.body && typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node && node.nodeType === 1) {
              applyAll(node);
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    window.addEventListener('load', function () {
      document.querySelectorAll('[data-far-sticker-studio]').forEach(function (root) {
        scheduleSettle(root, true);
      });
    }, { once: true });

    window.addEventListener('pageshow', function () {
      document.querySelectorAll('[data-far-sticker-studio]').forEach(function (root) {
        scheduleSettle(root, true);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    applyAll(event.target || document);
  });
})();